/**
 * Module Recouvrement — Overdue Accounts & Recovery (Kaizen System).
 * Tableau de bord admin : comptes en souffrance, aging, approbations, modèles, activité.
 * Garde-fous CASL + lancement contrôlé. Source : /app/memory/recouvrement/
 */
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  DollarSign, AlertTriangle, ShieldCheck, Clock, Loader2, RefreshCw, Send,
  StickyNote, CheckCircle2, Flag, Mail, Gavel, Activity, Layers, FileText, X,
  Handshake, Zap, Globe, CreditCard, PlayCircle, XCircle,
} from "lucide-react";

const CAD = (v) => new Intl.NumberFormat("fr-CA", { style: "currency", currency: "CAD" }).format(v || 0);

const STATUS_COLORS = {
  overdue: "bg-amber-500/20 text-amber-300",
  reminder_1_sent: "bg-amber-500/20 text-amber-300",
  reminder_2_sent: "bg-orange-500/20 text-orange-300",
  stronger_followup_sent: "bg-rose-500/20 text-rose-300",
  final_admin_review_required: "bg-red-600/30 text-red-200",
  promise_to_pay: "bg-blue-500/20 text-blue-300",
  broken_promise: "bg-rose-600/30 text-rose-200",
  paid_resolved: "bg-emerald-500/20 text-emerald-300",
};

const Kpi = ({ label, value, icon: Icon, accent, testid }) => (
  <div className={`rounded-2xl border p-4 ${accent}`} data-testid={testid}>
    <div className="flex items-center justify-between">
      <span className="text-[11px] uppercase tracking-wide text-zinc-400">{label}</span>
      <Icon className="w-4 h-4 opacity-70" />
    </div>
    <div className="mt-2 text-2xl font-bold">{value}</div>
  </div>
);

export default function RecoveryModule() {
  const [tab, setTab] = useState("now");
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [aging, setAging] = useState(null);
  const [approvals, setApprovals] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [events, setEvents] = useState([]);
  const [detail, setDetail] = useState(null);
  const [stripeVerif, setStripeVerif] = useState(null);
  const [promisesData, setPromisesData] = useState(null);
  const [cronData, setCronData] = useState(null);
  const [blockData, setBlockData] = useState(null);
  const [cronBusy, setCronBusy] = useState(false);
  const [lang, setLang] = useState("fr");

  // ➕ Ajout d'un retard de paiement manuel (hors facturation Stripe)
  const [manualOpen, setManualOpen] = useState(false);
  const [manualBusy, setManualBusy] = useState(false);
  const [manualForm, setManualForm] = useState({ client_name: "", amount_due: "", due_date: "", email: "", phone: "", note: "" });

  const submitManual = async () => {
    if (!manualForm.client_name.trim()) { toast.error("Nom du client requis."); return; }
    if (!(parseFloat(manualForm.amount_due) > 0)) { toast.error("Montant dû invalide."); return; }
    if (!manualForm.due_date) { toast.error("Date d'échéance requise."); return; }
    setManualBusy(true);
    try {
      const r = await api.post("/recovery/accounts/manual", { ...manualForm, amount_due: parseFloat(manualForm.amount_due) });
      toast.success(`Dossier créé : ${r.data.account.client_name} · ${r.data.account.amount_due} $ (${r.data.account.days_overdue} j de retard)`);
      setManualOpen(false);
      setManualForm({ client_name: "", amount_due: "", due_date: "", email: "", phone: "", note: "" });
      loadBootstrap();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec de la création."); }
    setManualBusy(false);
  };

  const loadBootstrap = async () => {
    setLoading(true);
    try {
      const r = await api.get("/recovery/dashboard/bootstrap");
      setData(r.data);
    } catch (e) { toast.error("Erreur de chargement du recouvrement"); }
    setLoading(false);
  };

  useEffect(() => { loadBootstrap(); }, []);

  const loadTab = async (t) => {
    setTab(t);
    try {
      if (t === "aging" && !aging) setAging((await api.get("/recovery/dashboard/aging")).data);
      if (t === "approvals") setApprovals((await api.get("/recovery/dashboard/approvals")).data.approvals);
      if (t === "templates" && !templates.length) setTemplates((await api.get("/recovery/templates")).data.templates);
      if (t === "activity") setEvents((await api.get("/recovery/dashboard/activity")).data.events);
      if (t === "verify") setStripeVerif((await api.get("/recovery/dashboard/stripe-verification")).data);
      if (t === "promises") setPromisesData((await api.get("/recovery/dashboard/promises")).data);
      if (t === "cron") setCronData((await api.get("/recovery/cron/status")).data);
      if (t === "blockages") setBlockData((await api.get("/recovery/dashboard/blockages")).data);
    } catch (e) { toast.error("Erreur de chargement"); }
  };

  const openDetail = async (id) => {
    try { setDetail((await api.get(`/recovery/accounts/${id}`)).data); }
    catch (e) { toast.error("Dossier introuvable"); }
  };

  const prepareReminder = async (id, templateId) => {
    try {
      const r = await api.post(`/recovery/accounts/${id}/prepare-reminder`, { template_id: templateId, language: lang });
      const st = r.data.status;
      if (st === "sent") toast.success("Courriel ENVOYÉ ✅");
      else if (r.data.queued_for_approval) toast.warning("Action sensible → file d'approbation admin");
      else if (st === "no_email") toast.warning("Aucun courriel au dossier — message non envoyé");
      else if (st === "send_failed") toast.error(`Échec d'envoi : ${r.data.detail === "no_gmail_account" ? "Gmail non connecté — reconnecte ton compte Gmail" : r.data.detail || "erreur"}`);
      else if (r.data.success) toast.success("Rappel préparé ✅");
      loadBootstrap();
      if (detail) openDetail(id);
    } catch (e) {
      toast.error(`Bloqué : ${e?.response?.data?.detail || "garde-fou actif"}`);
    }
  };

  const addNote = async (id) => {
    const text = window.prompt("Note interne :");
    if (!text) return;
    await api.post(`/recovery/accounts/${id}/notes`, { note_text: text });
    toast.success("Note ajoutée");
    if (detail) openDetail(id);
  };

  const requestReview = async (id) => {
    await api.post(`/recovery/accounts/${id}/request-admin-review`, { reason_summary: "Revue demandée depuis le dashboard" });
    toast.success("Revue admin demandée");
    loadBootstrap();
    if (detail) openDetail(id);
  };

  const markResolved = async (id) => {
    if (!window.confirm("Marquer ce dossier comme résolu (payé) ?")) return;
    await api.post(`/recovery/accounts/${id}/mark-resolved`);
    toast.success("Dossier résolu");
    loadBootstrap();
    setDetail(null);
  };

  const toggleFlag = async (id, key, value) => {
    await api.post(`/recovery/accounts/${id}/flags`, { flag_key: key, value });
    toast.success(`Drapeau ${key} ${value ? "activé" : "retiré"}`);
    loadBootstrap();
    if (detail) openDetail(id);
  };

  const decideApproval = async (approvalId, action) => {
    try {
      if (action === "approve") await api.post(`/recovery/approvals/${approvalId}/approve`);
      else {
        const reason = window.prompt("Motif de refus (obligatoire) :");
        if (!reason) return;
        await api.post(`/recovery/approvals/${approvalId}/reject`, { rejection_reason: reason });
      }
      toast.success("Décision enregistrée");
      loadTab("approvals");
      loadBootstrap();
    } catch (e) { toast.error("Action réservée aux admins"); }
  };

  const createArrangement = async (id, isArr) => {
    const date = window.prompt(`Date de ${isArr ? "l'arrangement" : "la promesse"} (AAAA-MM-JJ) :`);
    if (!date) return;
    const amt = window.prompt("Montant promis (optionnel) :");
    try {
      await api.post(`/recovery/accounts/${id}/create-arrangement`, { promise_date: date, promise_amount: amt ? parseFloat(amt) : null, arrangement: isArr });
      toast.success(isArr ? "Arrangement enregistré ✅" : "Promesse enregistrée ✅");
      if (tab === "promises") setPromisesData((await api.get("/recovery/dashboard/promises")).data);
      if (detail) openDetail(id);
      loadBootstrap();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const promiseDecision = async (id, kept) => {
    if (!window.confirm(kept ? "Marquer la promesse comme tenue (payé) ?" : "Marquer la promesse comme NON tenue ?")) return;
    await api.post(`/recovery/accounts/${id}/${kept ? "promise-kept" : "promise-broken"}`);
    toast.success(kept ? "Promesse tenue → dossier résolu" : "Promesse non tenue → revue admin");
    setPromisesData((await api.get("/recovery/dashboard/promises")).data);
    loadBootstrap();
  };

  const markVerified = async (id) => {
    await api.post(`/recovery/accounts/${id}/mark-verified`);
    toast.success("Dossier vérifié ✅");
    setStripeVerif((await api.get("/recovery/dashboard/stripe-verification")).data);
  };

  const runCronNow = async () => {
    if (!window.confirm("Lancer MAINTENANT l'envoi automatique du Rappel 1 aux comptes éligibles (0-7 j) ?")) return;
    setCronBusy(true);
    try {
      const r = await api.post("/recovery/cron/run-reminder1");
      const s = r.data.summary;
      if (s.disabled) toast.warning("Kill-switch actif (envois désactivés)");
      else if (s.no_gmail) toast.warning("Aucun compte Gmail connecté — reconnecte Gmail dans Intégrations");
      else toast.success(`Cron exécuté : ${s.sent} envoyé(s), ${s.skipped} ignoré(s), ${s.errors} erreur(s)`);
      setCronData((await api.get("/recovery/cron/status")).data);
      loadBootstrap();
    } catch (e) { toast.error("Action réservée aux admins"); }
    setCronBusy(false);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-amber-400" /></div>;

  const k = data?.kpis || {};
  const autoTpl = (templates.length ? templates : []).filter((t) => t.auto_send_allowed && t.enabled);

  return (
    <div className="space-y-6 text-zinc-100" data-testid="recovery-module">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><DollarSign className="w-6 h-6 text-amber-400" /> Recouvrement & Comptes en souffrance</h1>
          <p className="text-sm text-zinc-400">Suivi des soldes impayés · lancement contrôlé · garde-fous CASL & Stripe.</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-zinc-500">MAJ {data?.last_updated_at ? new Date(data.last_updated_at).toLocaleString("fr-CA") : "—"}</span>
          <div className="flex items-center rounded-lg bg-zinc-800 p-0.5" data-testid="recovery-lang-toggle" title="Langue des courriels">
            <Globe className="w-3.5 h-3.5 text-zinc-400 mx-1" />
            {["fr", "en"].map((l) => (
              <button key={l} onClick={() => setLang(l)} data-testid={`recovery-lang-${l}`}
                className={`px-2 py-1 rounded-md text-xs uppercase font-medium ${lang === l ? "bg-amber-500 text-zinc-900" : "text-zinc-400 hover:text-zinc-200"}`}>{l}</button>
            ))}
          </div>
          <button onClick={() => setManualOpen(true)} className="px-3 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-zinc-900 text-sm font-medium flex items-center gap-1" data-testid="recovery-add-manual"><DollarSign className="w-4 h-4" /> Ajouter un retard manuel</button>
          <button onClick={loadBootstrap} className="px-3 py-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-sm flex items-center gap-1" data-testid="recovery-refresh"><RefreshCw className="w-4 h-4" /> Actualiser</button>
        </div>
      </div>

      {/* Modale : ajout d'un retard manuel */}
      {manualOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setManualOpen(false)} data-testid="manual-account-modal">
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg rounded-2xl border border-zinc-700 bg-zinc-950 p-6 space-y-4">
            <div>
              <h3 className="text-lg font-bold text-amber-300 flex items-center gap-2"><DollarSign className="w-5 h-5" /> Ajouter un retard de paiement manuel</h3>
              <p className="text-xs text-zinc-400 mt-1">Pour un solde hors facturation Stripe (entente ponctuelle, ancien solde). Alimente les relances automatiques.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="col-span-2 text-xs text-zinc-400">Nom du client *
                <input value={manualForm.client_name} onChange={(e) => setManualForm((s) => ({ ...s, client_name: e.target.value }))} data-testid="manual-name" className="mt-1 w-full bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-100" placeholder="Ex. Elzé Bessette" />
              </label>
              <label className="text-xs text-zinc-400">Montant dû (CAD) *
                <input type="number" min="0" step="0.01" value={manualForm.amount_due} onChange={(e) => setManualForm((s) => ({ ...s, amount_due: e.target.value }))} data-testid="manual-amount" className="mt-1 w-full bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-100" placeholder="200" />
              </label>
              <label className="text-xs text-zinc-400">Date d'échéance *
                <input type="date" value={manualForm.due_date} onChange={(e) => setManualForm((s) => ({ ...s, due_date: e.target.value }))} data-testid="manual-due-date" className="mt-1 w-full bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-100" />
              </label>
              <label className="text-xs text-zinc-400">Courriel (facultatif)
                <input value={manualForm.email} onChange={(e) => setManualForm((s) => ({ ...s, email: e.target.value }))} data-testid="manual-email" className="mt-1 w-full bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-100" placeholder="client@courriel.com" />
              </label>
              <label className="text-xs text-zinc-400">Téléphone (facultatif)
                <input value={manualForm.phone} onChange={(e) => setManualForm((s) => ({ ...s, phone: e.target.value }))} data-testid="manual-phone" className="mt-1 w-full bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-100" placeholder="+1 819 ..." />
              </label>
              <label className="col-span-2 text-xs text-zinc-400">Note (facultatif)
                <textarea value={manualForm.note} onChange={(e) => setManualForm((s) => ({ ...s, note: e.target.value }))} data-testid="manual-note" rows={2} className="mt-1 w-full bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-100" placeholder="Ex. 2 mois de retard, courriel envoyé le…" />
              </label>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setManualOpen(false)} className="px-3 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-sm" data-testid="manual-cancel">Annuler</button>
              <button onClick={submitManual} disabled={manualBusy} data-testid="manual-submit" className="px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-zinc-900 text-sm font-medium disabled:opacity-60">{manualBusy ? "Création…" : "Créer le dossier"}</button>
            </div>
          </div>
        </div>
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <Kpi label="Total dû" value={CAD(k.total_amount_due)} icon={DollarSign} accent="border-amber-500/30 bg-amber-500/5" testid="kpi-total-due" />
        <Kpi label="Comptes" value={k.accounts_with_balance || 0} icon={Layers} accent="border-zinc-700 bg-zinc-900" testid="kpi-accounts" />
        <Kpi label="30+ jours" value={k.accounts_30_plus || 0} icon={Clock} accent="border-orange-500/30 bg-orange-500/5" testid="kpi-30" />
        <Kpi label="60+ jours" value={k.accounts_60_plus || 0} icon={Clock} accent="border-rose-500/30 bg-rose-500/5" testid="kpi-60" />
        <Kpi label="90+ jours" value={k.accounts_90_plus || 0} icon={AlertTriangle} accent="border-red-600/30 bg-red-600/5" testid="kpi-90" />
        <Kpi label="Approb. en attente" value={k.pending_approvals || 0} icon={Gavel} accent="border-blue-500/30 bg-blue-500/5" testid="kpi-approvals" />
        <Kpi label="Contacts bloqués" value={k.blocked_contact_count || 0} icon={ShieldCheck} accent="border-zinc-700 bg-zinc-900" testid="kpi-blocked" />
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-zinc-800 flex-wrap">
        {[["now", "Action requise", AlertTriangle], ["aging", "Aging", Clock], ["blockages", "Blocages 90+", Flag], ["approvals", "Approbations", Gavel], ["verify", "Vérification", CreditCard], ["promises", "Promesses", Handshake], ["templates", "Modèles", Mail], ["cron", "Automatisation", Zap], ["activity", "Activité", Activity]].map(([id, label, Icon]) => (
          <button key={id} onClick={() => loadTab(id)} data-testid={`recovery-tab-${id}`}
            className={`px-3 py-2 text-sm flex items-center gap-1 border-b-2 -mb-px ${tab === id ? "border-amber-400 text-amber-300" : "border-transparent text-zinc-400 hover:text-zinc-200"}`}>
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {/* Action Required Now */}
      {tab === "now" && (
        <div className="overflow-x-auto rounded-xl border border-zinc-800" data-testid="recovery-action-table">
          <table className="w-full text-sm">
            <thead className="bg-zinc-900 text-zinc-400 text-xs uppercase">
              <tr><th className="text-left p-3">Membre</th><th className="text-left p-3">Solde</th><th className="text-left p-3">Retard</th><th className="text-left p-3">Statut</th><th className="text-left p-3">Action recommandée</th><th className="text-right p-3">Actions</th></tr>
            </thead>
            <tbody>
              {(data?.action_now || []).map((a) => (
                <tr key={a.account_id} className="border-t border-zinc-800/60 hover:bg-zinc-900/40" data-testid={`recovery-row-${a.account_id}`}>
                  <td className="p-3">
                    <button onClick={() => openDetail(a.account_id)} className="font-medium text-amber-100 hover:underline">{a.client_name}</button>
                    <div className="text-[11px] text-zinc-500">{a.discipline}</div>
                  </td>
                  <td className="p-3 font-semibold">{CAD(a.amount_due)}</td>
                  <td className="p-3">{a.days_overdue} j</td>
                  <td className="p-3"><span className={`px-2 py-0.5 rounded-full text-[11px] ${STATUS_COLORS[a.recovery_status] || "bg-zinc-700 text-zinc-300"}`}>{a.recovery_status}</span>
                    {a.pending_admin_review && <span className="ml-1 px-2 py-0.5 rounded-full text-[11px] bg-blue-500/20 text-blue-300">revue</span>}
                  </td>
                  <td className="p-3 text-xs text-zinc-400">{a.recommended_action}</td>
                  <td className="p-3">
                    <div className="flex items-center gap-1 justify-end">
                      <button title="Préparer Rappel 1" onClick={() => prepareReminder(a.account_id, "TPL-REC-001")} className="px-2 py-1 rounded bg-amber-600/30 hover:bg-amber-600/50 text-amber-100 text-xs flex items-center gap-1" data-testid={`recovery-remind-${a.account_id}`}><Send className="w-3 h-3" /> Rappel</button>
                      <button title="Note" onClick={() => addNote(a.account_id)} className="px-2 py-1 rounded bg-zinc-800 hover:bg-zinc-700 text-xs"><StickyNote className="w-3 h-3" /></button>
                      <button title="Revue admin" onClick={() => requestReview(a.account_id)} className="px-2 py-1 rounded bg-blue-600/30 hover:bg-blue-600/50 text-xs"><Gavel className="w-3 h-3" /></button>
                      <button title="Résolu" onClick={() => markResolved(a.account_id)} className="px-2 py-1 rounded bg-emerald-600/30 hover:bg-emerald-600/50 text-xs"><CheckCircle2 className="w-3 h-3" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {(!data?.action_now || data.action_now.length === 0) && (
                <tr><td colSpan={6} className="p-8 text-center text-zinc-500">Aucun compte en souffrance 🎉</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Aging */}
      {tab === "aging" && aging && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {Object.entries(aging.buckets).map(([b, v]) => (
              <div key={b} className="rounded-xl border border-zinc-800 bg-zinc-900 p-3" data-testid={`aging-bucket-${b}`}>
                <div className="text-xs text-zinc-400">{b.replace("_", "-")} jours</div>
                <div className="text-lg font-bold">{v.count}</div>
                <div className="text-xs text-amber-300">{CAD(v.total)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Approbations */}
      {tab === "approvals" && (
        <div className="space-y-2" data-testid="recovery-approvals-list">
          {approvals.length === 0 && <p className="text-zinc-500 text-sm p-4">Aucune approbation en attente.</p>}
          {approvals.map((ap) => (
            <div key={ap.approval_id} className="flex items-center justify-between gap-3 rounded-xl border border-zinc-800 bg-zinc-900 p-3 flex-wrap">
              <div>
                <div className="text-sm font-medium">{ap.client_name} · <span className="text-amber-300">{ap.requested_action}</span></div>
                <div className="text-xs text-zinc-400">{ap.reason_summary} · priorité {ap.priority}</div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => decideApproval(ap.approval_id, "approve")} className="px-3 py-1 rounded bg-emerald-600/30 hover:bg-emerald-600/50 text-xs">Approuver</button>
                <button onClick={() => decideApproval(ap.approval_id, "reject")} className="px-3 py-1 rounded bg-rose-600/30 hover:bg-rose-600/50 text-xs">Refuser</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modèles */}
      {tab === "templates" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="recovery-templates-list">
          {templates.map((t) => (
            <div key={t.template_id} className="rounded-xl border border-zinc-800 bg-zinc-900 p-3">
              <div className="flex items-center justify-between">
                <div className="font-medium text-sm flex items-center gap-2"><FileText className="w-4 h-4 text-amber-400" /> {t.name}</div>
                {t.enabled && t.auto_send_allowed ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300">AUTO</span>
                  : <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-300">ADMIN</span>}
              </div>
              <div className="text-xs text-zinc-400 mt-1">{t.subject_fr}</div>
              <div className="text-[11px] text-zinc-500 mt-1">{t.template_id} · {t.category} · {t.days_min}-{t.days_max}j</div>
            </div>
          ))}
        </div>
      )}

      {/* Activité */}
      {tab === "activity" && (
        <div className="space-y-1" data-testid="recovery-activity-list">
          {events.length === 0 && <p className="text-zinc-500 text-sm p-4">Aucune activité récente.</p>}
          {events.map((e) => (
            <div key={e.event_id} className="flex items-center gap-3 text-xs border-b border-zinc-800/50 py-2">
              <span className="text-zinc-500 w-36 shrink-0">{new Date(e.created_at).toLocaleString("fr-CA")}</span>
              <span className="text-amber-300 w-48 shrink-0">{e.event_type}</span>
              <span className="text-zinc-300">{e.title}</span>
              <span className="text-zinc-600 ml-auto">{e.actor}</span>
            </div>
          ))}
        </div>
      )}

      {/* Blocages 90+ jours */}
      {tab === "blockages" && blockData && (
        <div className="space-y-4" data-testid="recovery-blockages">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="rounded-xl border border-rose-500/30 bg-rose-500/5 p-3"><div className="text-xs text-zinc-400">Comptes 90+ jours</div><div className="text-lg font-bold text-rose-300">{blockData.stats.count_90_plus}</div></div>
            <div className="rounded-xl border border-rose-500/30 bg-rose-500/5 p-3"><div className="text-xs text-zinc-400">Montant 90+</div><div className="text-lg font-bold text-rose-300">{CAD(blockData.stats.amount_90_plus)}</div></div>
            <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3"><div className="text-xs text-zinc-400">Accès bloqués</div><div className="text-lg font-bold text-amber-300">{blockData.stats.count_blocked}</div></div>
            <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3"><div className="text-xs text-zinc-400">Montant bloqué</div><div className="text-lg font-bold text-amber-300">{CAD(blockData.stats.amount_blocked)}</div></div>
          </div>
          <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-4">
            <div className="text-xs uppercase text-zinc-500 mb-2">Ancienneté du solde (buckets)</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {blockData.aging_buckets.map((b) => (
                <div key={b.bucket} className="rounded-lg bg-zinc-800/50 p-2 text-center"><div className="text-xs text-zinc-400">{b.bucket} j</div><div className="font-semibold">{b.count}</div><div className="text-[11px] text-zinc-500">{CAD(b.amount)}</div></div>
              ))}
            </div>
          </div>
          <div className="overflow-x-auto rounded-xl border border-zinc-800">
            <table className="w-full text-sm">
              <thead className="bg-zinc-900 text-zinc-400 text-xs uppercase"><tr><th className="text-left p-3">Membre</th><th className="text-left p-3">Solde</th><th className="text-left p-3">Jours</th><th className="text-left p-3">Accès</th><th className="text-left p-3">Statut</th></tr></thead>
              <tbody>
                {blockData.accounts_90_plus.map((r) => (
                  <tr key={r.account_id} className="border-t border-zinc-800/60 hover:bg-zinc-900/40">
                    <td className="p-3"><button onClick={() => openDetail(r.account_id)} className="text-amber-100 hover:underline">{r.client_name}</button></td>
                    <td className="p-3 font-semibold text-rose-300">{CAD(r.amount_due)}</td>
                    <td className="p-3">{r.days_overdue} j</td>
                    <td className="p-3 text-xs">{r.access_status || "—"}</td>
                    <td className="p-3 text-xs">{r.recovery_status}</td>
                  </tr>
                ))}
                {blockData.accounts_90_plus.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-zinc-500">Aucun compte à 90 jours et plus 🎉</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Vérification Stripe */}
      {tab === "verify" && stripeVerif && (
        <div className="space-y-4" data-testid="recovery-verify">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-3"><div className="text-xs text-zinc-400">Événements Stripe reçus</div><div className="text-lg font-bold">{stripeVerif.stats.total_events}</div></div>
            <div className="rounded-xl border border-rose-500/30 bg-rose-500/5 p-3"><div className="text-xs text-zinc-400">Écarts à vérifier</div><div className="text-lg font-bold text-rose-300">{stripeVerif.stats.mismatches}</div></div>
            <div className={`rounded-xl border p-3 ${stripeVerif.stats.webhook_secret_configured ? "border-emerald-500/30 bg-emerald-500/5" : "border-amber-500/30 bg-amber-500/5"}`}>
              <div className="text-xs text-zinc-400">Webhook signé</div>
              <div className={`text-sm font-semibold ${stripeVerif.stats.webhook_secret_configured ? "text-emerald-300" : "text-amber-300"}`}>{stripeVerif.stats.webhook_secret_configured ? "Configuré ✅" : "Secret non configuré"}</div>
              <div className="text-[10px] text-zinc-500 mt-1 break-all">URL : {stripeVerif.stats.webhook_url}</div>
            </div>
          </div>
          <div className="overflow-x-auto rounded-xl border border-zinc-800">
            <table className="w-full text-sm">
              <thead className="bg-zinc-900 text-zinc-400 text-xs uppercase"><tr><th className="text-left p-3">Membre</th><th className="text-left p-3">Solde</th><th className="text-left p-3">Facturation</th><th className="text-left p-3">Recouvrement</th><th className="text-left p-3">Dernier évén. Stripe</th><th className="text-left p-3">Écart</th><th className="text-right p-3">Action</th></tr></thead>
              <tbody>
                {stripeVerif.rows.map((r) => (
                  <tr key={r.account_id} className="border-t border-zinc-800/60 hover:bg-zinc-900/40">
                    <td className="p-3"><button onClick={() => openDetail(r.account_id)} className="text-amber-100 hover:underline">{r.client_name}</button></td>
                    <td className="p-3 font-semibold">{CAD(r.amount_due)}</td>
                    <td className="p-3 text-xs">{r.billing_status || "—"}</td>
                    <td className="p-3 text-xs">{r.recovery_status}</td>
                    <td className="p-3 text-xs text-zinc-400">{r.last_stripe_event_type || "—"}</td>
                    <td className="p-3 text-xs">{r.mismatch ? <span className="text-rose-300">{r.mismatch}</span> : <span className="text-emerald-400">OK</span>}</td>
                    <td className="p-3 text-right"><button onClick={() => markVerified(r.account_id)} className="px-2 py-1 rounded bg-emerald-600/30 hover:bg-emerald-600/50 text-xs flex items-center gap-1 ml-auto" data-testid={`verify-${r.account_id}`}><ShieldCheck className="w-3 h-3" /> Vérifié</button></td>
                  </tr>
                ))}
                {stripeVerif.rows.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-zinc-500">Aucun écart Stripe détecté 🎉</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Promesses & arrangements */}
      {tab === "promises" && promisesData && (
        <div className="space-y-4" data-testid="recovery-promises">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-3"><div className="text-xs text-zinc-400">Total</div><div className="text-lg font-bold">{promisesData.stats.total}</div></div>
            <div className="rounded-xl border border-blue-500/30 bg-blue-500/5 p-3"><div className="text-xs text-zinc-400">À venir</div><div className="text-lg font-bold text-blue-300">{promisesData.stats.upcoming}</div></div>
            <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3"><div className="text-xs text-zinc-400">En retard</div><div className="text-lg font-bold text-amber-300">{promisesData.stats.overdue}</div></div>
            <div className="rounded-xl border border-rose-500/30 bg-rose-500/5 p-3"><div className="text-xs text-zinc-400">Non tenues</div><div className="text-lg font-bold text-rose-300">{promisesData.stats.broken}</div></div>
          </div>
          <div className="overflow-x-auto rounded-xl border border-zinc-800">
            <table className="w-full text-sm">
              <thead className="bg-zinc-900 text-zinc-400 text-xs uppercase"><tr><th className="text-left p-3">Membre</th><th className="text-left p-3">Solde</th><th className="text-left p-3">Date promise</th><th className="text-left p-3">Type</th><th className="text-left p-3">Statut</th><th className="text-right p-3">Actions</th></tr></thead>
              <tbody>
                {promisesData.rows.map((r) => (
                  <tr key={r.account_id} className="border-t border-zinc-800/60 hover:bg-zinc-900/40">
                    <td className="p-3"><button onClick={() => openDetail(r.account_id)} className="text-amber-100 hover:underline">{r.client_name}</button></td>
                    <td className="p-3 font-semibold">{CAD(r.amount_due)}</td>
                    <td className="p-3">{r.promise_date || "—"}</td>
                    <td className="p-3 text-xs">{r.arrangement_active ? "Arrangement" : "Promesse"}</td>
                    <td className="p-3"><span className={`px-2 py-0.5 rounded-full text-[11px] ${r.promise_status === "broken" ? "bg-rose-600/30 text-rose-200" : r.promise_status === "overdue" ? "bg-amber-500/20 text-amber-300" : "bg-blue-500/20 text-blue-300"}`}>{r.promise_status}</span></td>
                    <td className="p-3"><div className="flex items-center gap-1 justify-end">
                      <button title="Tenue (payé)" onClick={() => promiseDecision(r.account_id, true)} className="px-2 py-1 rounded bg-emerald-600/30 hover:bg-emerald-600/50 text-xs flex items-center gap-1" data-testid={`promise-kept-${r.account_id}`}><CheckCircle2 className="w-3 h-3" /></button>
                      <button title="Non tenue" onClick={() => promiseDecision(r.account_id, false)} className="px-2 py-1 rounded bg-rose-600/30 hover:bg-rose-600/50 text-xs flex items-center gap-1" data-testid={`promise-broken-${r.account_id}`}><XCircle className="w-3 h-3" /></button>
                    </div></td>
                  </tr>
                ))}
                {promisesData.rows.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-zinc-500">Aucune promesse active. Ajoute-en depuis la fiche d'un compte.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Automatisation (Cron) */}
      {tab === "cron" && cronData && (
        <div className="space-y-4" data-testid="recovery-cron">
          <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-4 flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="text-sm font-medium flex items-center gap-2"><Zap className="w-4 h-4 text-amber-400" /> Envoi automatique du Rappel 1</div>
              <div className="text-xs text-zinc-400 mt-1">{cronData.schedule}</div>
              <div className="text-xs mt-1">Kill-switch : {cronData.sends_enabled ? <span className="text-emerald-300">envois activés</span> : <span className="text-rose-300">envois désactivés</span>}</div>
            </div>
            <button onClick={runCronNow} disabled={cronBusy} className="px-4 py-2 rounded-lg bg-amber-600/30 hover:bg-amber-600/50 text-amber-100 text-sm flex items-center gap-2 disabled:opacity-50" data-testid="cron-run-now">
              {cronBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />} Exécuter maintenant
            </button>
          </div>
          <div>
            <div className="text-xs uppercase text-zinc-500 mb-2">Dernières exécutions</div>
            <div className="space-y-1" data-testid="cron-runs-list">
              {(cronData.runs || []).map((r) => (
                <div key={r.run_id} className="flex items-center gap-3 text-xs border-b border-zinc-800/50 py-2">
                  <span className="text-zinc-500 w-40 shrink-0">{new Date(r.at).toLocaleString("fr-CA")}</span>
                  <span className="text-emerald-300">{r.sent} envoyé(s)</span>
                  <span className="text-zinc-400">{r.skipped} ignoré(s)</span>
                  {r.errors > 0 && <span className="text-rose-300">{r.errors} erreur(s)</span>}
                  {r.disabled && <span className="text-amber-300">kill-switch actif</span>}
                  {r.no_gmail && <span className="text-amber-300">Gmail non connecté</span>}
                </div>
              ))}
              {(!cronData.runs || cronData.runs.length === 0) && <p className="text-zinc-500 text-sm">Aucune exécution enregistrée pour l'instant.</p>}
            </div>
          </div>
        </div>
      )}

      {/* Detail drawer */}
      {detail && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/60" onClick={() => setDetail(null)}>
          <div className="w-full max-w-md bg-zinc-950 border-l border-zinc-800 h-full overflow-y-auto p-5" onClick={(e) => e.stopPropagation()} data-testid="recovery-detail-drawer">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">{detail.account.client_name}</h3>
              <button onClick={() => setDetail(null)}><X className="w-5 h-5 text-zinc-400" /></button>
            </div>
            <div className="space-y-1 text-sm">
              <div>Solde : <span className="font-semibold text-amber-300">{CAD(detail.account.amount_due)}</span></div>
              <div>Retard : {detail.account.days_overdue} jours · {detail.account.recovery_status}</div>
              <div>Échéance : {detail.account.due_date || "—"}</div>
              <div className="text-zinc-400 text-xs">{detail.account.email || "pas de courriel"} · {detail.account.phone || "—"}</div>
            </div>

            {/* Flags */}
            <div className="mt-4">
              <div className="text-xs uppercase text-zinc-500 mb-1">Drapeaux</div>
              <div className="flex gap-2 flex-wrap">
                {["do_not_auto_contact", "written_only_contact", "dispute_flag"].map((fk) => (
                  <button key={fk} onClick={() => toggleFlag(detail.account.account_id, fk, !detail.account.flags?.[fk])}
                    className={`px-2 py-1 rounded text-[11px] flex items-center gap-1 ${detail.account.flags?.[fk] ? "bg-rose-600/40 text-rose-100" : "bg-zinc-800 text-zinc-400"}`}>
                    <Flag className="w-3 h-3" /> {fk}
                  </button>
                ))}
              </div>
            </div>

            {/* Préparer rappel */}
            <div className="mt-4">
              <div className="text-xs uppercase text-zinc-500 mb-1">Préparer un courriel</div>
              <select onChange={(e) => { if (e.target.value) prepareReminder(detail.account.account_id, e.target.value); e.target.value = ""; }}
                className="w-full bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-sm" data-testid="recovery-detail-template-select" defaultValue="">
                <option value="">— Choisir un modèle —</option>
                {(templates.length ? templates : autoTpl).map((t) => <option key={t.template_id} value={t.template_id}>{t.name}{t.admin_approval_required ? " (admin)" : ""}</option>)}
              </select>
            </div>

            {/* Timeline */}
            <div className="mt-4">
              <div className="text-xs uppercase text-zinc-500 mb-1">Historique</div>
              <div className="space-y-1">
                {(detail.timeline || []).map((e) => (
                  <div key={e.event_id} className="text-[11px] text-zinc-400 border-b border-zinc-800/50 py-1">
                    <span className="text-amber-300">{e.event_type}</span> — {e.title} <span className="text-zinc-600">({new Date(e.created_at).toLocaleDateString("fr-CA")})</span>
                  </div>
                ))}
                {(detail.timeline || []).length === 0 && <p className="text-zinc-600 text-xs">Aucun événement.</p>}
              </div>
            </div>

            {/* Promesse / Arrangement */}
            <div className="mt-4">
              <div className="text-xs uppercase text-zinc-500 mb-1">Promesse / Arrangement</div>
              <div className="flex gap-2">
                <button onClick={() => createArrangement(detail.account.account_id, false)} className="flex-1 px-2 py-1.5 rounded bg-blue-600/30 hover:bg-blue-600/50 text-xs flex items-center justify-center gap-1" data-testid="drawer-add-promise"><Handshake className="w-3 h-3" /> Promesse</button>
                <button onClick={() => createArrangement(detail.account.account_id, true)} className="flex-1 px-2 py-1.5 rounded bg-indigo-600/30 hover:bg-indigo-600/50 text-xs flex items-center justify-center gap-1" data-testid="drawer-add-arrangement"><Handshake className="w-3 h-3" /> Arrangement</button>
              </div>
            </div>

            <div className="mt-5 flex gap-2">
              <button onClick={() => addNote(detail.account.account_id)} className="flex-1 px-3 py-2 rounded bg-zinc-800 hover:bg-zinc-700 text-sm flex items-center justify-center gap-1"><StickyNote className="w-4 h-4" /> Note</button>
              <button onClick={() => markResolved(detail.account.account_id)} className="flex-1 px-3 py-2 rounded bg-emerald-600/30 hover:bg-emerald-600/50 text-sm flex items-center justify-center gap-1"><CheckCircle2 className="w-4 h-4" /> Résolu</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
